import { useEffect, useMemo, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { startOfDay, addDays, eachDayOfInterval, format as formatDate, isWeekend } from 'date-fns';
import { useWorkspaces } from '@/hooks/useWorkspaces';
import { useAuth } from '@/contexts/AuthContext';
import { useAnalyticsFilters } from '@/hooks/useAnalyticsFilters';
import { useAnalyticsKPIs } from '@/hooks/useAnalyticsKPIs';
import { useCallOutcomesBreakdown } from '@/hooks/useCallOutcomesBreakdown';
import { useConnectionsByTypeBreakdown } from '@/hooks/useConnectionsByTypeBreakdown';
import { useContactOptions } from '@/hooks/useContactOptions';
import { resolvePresetRange, resolveCompareRange } from '@/lib/analytics/ranges';
import { queryKeys } from '@/lib/query-keys';
import { supabase } from '@/lib/supabase';
import { Skeleton } from '@/components/ui/skeleton';
import { AnalyticsFilters } from '@/components/analytics/AnalyticsFilters';
import { AnalyticsKPIRow } from '@/components/analytics/AnalyticsKPIRow';
import { AnalyticsChartCard } from '@/components/analytics/AnalyticsChartCard';
import { CustomChartCard } from '@/components/analytics/CustomChartCard';
import { AnalyticsLeaderboard } from '@/components/analytics/AnalyticsLeaderboard';
import { AnalyticsDrilldownPanel } from '@/components/analytics/AnalyticsDrilldownPanel';
import { CallOutcomesChart } from '@/components/analytics/charts/CallOutcomesChart';
import { CallOutcomesRibbon } from '@/components/analytics/charts/CallOutcomesRibbon';
import type { MetricKey } from '@/lib/analytics/metrics';

export default function AnalyticsPage() {
  const { currentWorkspace, loading: wsLoading } = useWorkspaces();
  if (wsLoading || !currentWorkspace) {
    return (
      <div className="p-4 space-y-4">
        <Skeleton className="h-8 w-48" />
        <Skeleton className="h-32 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }
  return <AnalyticsPageContent />;
}

function AnalyticsPageContent() {
  const { user } = useAuth();
  const { currentWorkspace } = useWorkspaces();
  const queryClient = useQueryClient();
  const ws = currentWorkspace!;
  const role = ws.role ?? 'member';
  const isAdmin = role === 'owner' || role === 'admin';
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const { filters, setFilters } = useAnalyticsFilters({
    isAdmin,
    currentUserId: user?.id ?? '',
  });

  // Midnight rollover tick for the "today" range. Declared before `range`
  // useMemo so it can appear in that hook's dependency array.
  const [dayKey, setDayKey] = useState(() => formatDate(new Date(), 'yyyy-MM-dd'));

  // Resolve current + comparison ranges. Memoized on filter inputs so the
  // computed Dates (and their toISOString()s) are stable across renders.
  // Without memoization, `resolvePresetRange` reads `new Date()` for `now`
  // every render → new Dates → new query keys → React Query starts a fresh
  // fetch on every render → cards stay in loading state forever.
  const range = useMemo(() => {
    return resolvePresetRange(filters.range, {
      from: filters.from ? new Date(filters.from) : undefined,
      to: filters.to ? new Date(filters.to) : undefined,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.range, filters.from, filters.to, dayKey]);

  const compareRange = useMemo(() => {
    if (filters.compare !== 'on') return null;
    return resolveCompareRange(filters.range, range);
  }, [filters.compare, filters.range, range]);

  // Workspace switch invalidation + cancel + close drilldown
  useEffect(() => {
    queryClient.cancelQueries({ queryKey: queryKeys.analytics.all });
    queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
    if (filters.drilldown) {
      setFilters({ drilldown: null, drilldown_segment: null });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.id]);

  // Midnight rollover for the "today" range: bump dayKey at local midnight so
  // resolvePresetRange recomputes and analytics queries refetch with the new
  // day's bounds. dayKey is intentionally a tick (not consumed in body).
  useEffect(() => {
    if (filters.range !== 'today') return;
    const now = new Date();
    const msUntilMidnight = startOfDay(addDays(now, 1)).getTime() - now.getTime() + 1_000;
    const handle = setTimeout(() => {
      setDayKey(formatDate(new Date(), 'yyyy-MM-dd'));
      queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
    }, msUntilMidnight);
    return () => clearTimeout(handle);
  }, [filters.range, dayKey, queryClient]);

  // One-shot cleanup of any pre-existing `call_outcomes` collapse entry.
  // The cardKey is reused for the new full-width combined card, so without
  // this migration users who collapsed the OLD half-width card would land on
  // the NEW headline card already collapsed.
  useEffect(() => {
    const raw = localStorage.getItem(`analytics:cardCollapse:${ws.id}`);
    if (!raw) return;
    try {
      const state = JSON.parse(raw) as Record<string, boolean>;
      if ('call_outcomes' in state) {
        delete state['call_outcomes'];
        localStorage.setItem(`analytics:cardCollapse:${ws.id}`, JSON.stringify(state));
      }
    } catch {
      // Corrupt JSON; AnalyticsChartCard tolerates it.
    }
  }, [ws.id]);

  // Realtime: react to current user's role changing in this workspace.
  // Promotion -> leaderboard appears next fetch. Demotion -> isAdmin gate flips
  // and leaderboard disappears on re-render.
  useEffect(() => {
    if (!user?.id) return;
    const channel = supabase
      .channel(`analytics-role-${ws.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'workspace_members',
          filter: `workspace_id=eq.${ws.id}`,
        },
        (payload) => {
          const newRow = payload.new as { user_id?: string };
          if (newRow.user_id === user.id) {
            queryClient.invalidateQueries({ queryKey: queryKeys.analytics.all });
          }
        },
      )
      .subscribe();
    return () => {
      void channel.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ws.id, user?.id]);

  const sharedArgs = {
    workspaceId: ws.id,
    from: range.from,
    to: range.to,
    compareFrom: compareRange?.from ?? null,
    compareTo: compareRange?.to ?? null,
    manualGranularity: filters.granularity,
    userFilter: filters.user,
    tz,
  };

  const kpis = useAnalyticsKPIs({
    workspaceId: ws.id,
    from: range.from,
    to: range.to,
    compareFrom: compareRange?.from ?? null,
    compareTo: compareRange?.to ?? null,
    userFilter: filters.user,
    tz,
  });

  const callOutcomes = useCallOutcomesBreakdown({
    workspaceId: ws.id,
    from: range.from,
    to: range.to,
    compareFrom: compareRange?.from ?? null,
    compareTo: compareRange?.to ?? null,
    userFilter: filters.user,
    tz,
  });
  const connectionsByType = useConnectionsByTypeBreakdown(sharedArgs);

  const referralsData = useMemo(() => {
    const buckets = connectionsByType.buckets ?? [];
    const current = buckets
      .filter((b) => b.segment === 'Referral')
      .reduce((s, b) => s + (b.current ?? 0), 0);
    const previous = buckets
      .filter((b) => b.segment === 'Referral')
      .reduce((s, b) => s + (b.previous ?? 0), 0);
    function deltaPct(c: number, p: number): number | null {
      // Mirror Calls KPI behavior: suppress the delta line entirely when both
      // periods are zero (avoids a green "0.0% vs prev period" on empty data).
      if (p === 0 && c === 0) return null;
      if (p === 0) return null;
      return Math.round(((c - p) / p) * 1000) / 10;
    }
    return { current, previous, deltaPct: deltaPct(current, previous) };
  }, [connectionsByType.buckets]);

  // Meaningful Target: 10 meaningful conversations per working day in the range.
  // Memoized so the tile value is stable across renders.
  const TARGET_PER_WORKING_DAY = 10;
  const meaningfulTargetData = useMemo(() => {
    const workingDays = eachDayOfInterval({ start: range.from, end: range.to }).filter(
      (d) => !isWeekend(d),
    ).length;
    const target = Math.max(1, workingDays) * TARGET_PER_WORKING_DAY;
    const current = kpis.data?.meaningfulCalls.current ?? 0;
    const pct = target > 0 ? Math.round((current / target) * 100) : 0;
    return { current, target, pct };
  }, [range.from, range.to, kpis.data?.meaningfulCalls.current]);

  function openDrilldown(metric: MetricKey, segment: string | null = null) {
    // drilldown_segment regex requires leading-letter + non-PII; pass null if segment doesn't match
    const safeSegment = segment && /^[A-Za-z][A-Za-z0-9_ ]*$/.test(segment) ? segment : null;
    setFilters({ drilldown: metric, drilldown_segment: safeSegment });
  }

  function handleOutcomeBarClick(outcome: string) {
    // Unrecorded calls have call_outcome IS NULL; the existing drilldown RPC
    // strict-matches segment, so it can't surface those rows. v1: skip click.
    if (outcome === 'no_outcome') return;
    openDrilldown('calls', outcome);
  }

  // Filter option lists come from the workspace catalog (contact_options
  // table merged with hardcoded defaults), not the observed-data breakdowns.
  // The breakdowns only surface values present in the current date range,
  // which means newly-added or rarely-used outcomes never appear in filters.
  const { options: contactOptions } = useContactOptions();
  const callOutcomeOptions = useMemo(
    () => contactOptions.call_outcome.map((v) => ({ value: v, label: v })),
    [contactOptions.call_outcome],
  );
  const relationshipTypeOptions = useMemo(
    () => contactOptions.relationship.map((v) => ({ value: v, label: v })),
    [contactOptions.relationship],
  );

  // Workspace members list isn't exposed by useWorkspaces; pass empty array.
  // The user filter dropdown will only show "All workspace" + "Just me".
  const wsMembers: { user_id: string; display_name: string }[] = [];

  return (
    <div className="flex flex-col h-full w-full">
      <div className="p-4">
        <h1 className="text-2xl font-bold">Analytics</h1>
      </div>

      <AnalyticsFilters
        filters={filters}
        setFilters={setFilters}
        isAdmin={isAdmin}
        members={wsMembers}
        currentUserId={user?.id ?? ''}
      />

      <div className="p-4 space-y-4">
        <AnalyticsKPIRow
          data={kpis.data}
          isLoading={kpis.isLoading || connectionsByType.isLoading}
          error={kpis.error}
          onClickCalls={() => openDrilldown('calls')}
          referralsData={referralsData}
          onClickReferrals={() => openDrilldown('new_connections', 'Referral')}
          meaningfulTarget={meaningfulTargetData}
          onClickMeaningfulTarget={() => openDrilldown('meaningful_calls')}
        />

        <AnalyticsChartCard
          cardKey="call_outcomes"
          workspaceId={ws.id}
          title="Call outcomes"
          isLoading={callOutcomes.isLoading}
          error={callOutcomes.error}
          fullWidth
        >
          <CallOutcomesRibbon
            data={callOutcomes}
            canonicalOrder={contactOptions.call_outcome}
          />
          <div className="h-px bg-border my-3" />
          <CallOutcomesChart
            data={callOutcomes}
            canonicalOrder={contactOptions.call_outcome}
            onClickSegment={handleOutcomeBarClick}
          />
        </AnalyticsChartCard>

        <CustomChartCard
          shared={sharedArgs}
          outcomeOptions={callOutcomeOptions}
          relationshipOptions={relationshipTypeOptions}
          workspaceId={ws.id}
          userId={user?.id ?? ''}
          isAdmin={isAdmin}
        />

        {isAdmin && (
          <AnalyticsLeaderboard
            workspaceId={ws.id}
            filters={filters}
            role={role}
            tz={tz}
            fromDate={range.from}
            toDate={range.to}
          />
        )}
      </div>

      <AnalyticsDrilldownPanel
        metric={filters.drilldown}
        segment={filters.drilldown_segment}
        filters={filters}
        workspaceId={ws.id}
        fromDate={range.from}
        toDate={range.to}
        tz={tz}
        open={!!filters.drilldown}
        onClose={() => setFilters({ drilldown: null, drilldown_segment: null })}
      />
    </div>
  );
}
